"use client";

/**
 * ResearchMock — animated mock of the askFinz Research workspace for the
 * /apps/research marketing page.
 *
 * Pure presentation. Left rail: sources sidebar (4 source cards). Centre:
 * draft pane where the cursor mid-paragraph types out a sentence and a
 * citation pill `[3]` materialises next to the cited clause. Bottom: agent
 * panel ticking "found 38 sources → ranking → drafting → cited 4".
 *
 * NOT the real research workspace (research.askfinz.ai/app/page.tsx and
 * the Pages/Decks/Tables editors built on tiptap). Lives in askfinz.com
 * only. The real product is heavy with Yjs/tiptap/auth — this mock just
 * has to look the part.
 */
import { useEffect, useRef, useState } from "react";
import {
  Globe2,
  Newspaper,
  Building2,
  FileText,
  Sparkles,
  Bot,
  CheckCircle2,
  Play,
  Search,
  ArrowUpRight,
  Quote,
} from "lucide-react";
import { ScrambleText } from "@/components/marketing/animations";

type Source = {
  id: number;
  org: string;
  title: string;
  kind: "intergov" | "thinktank" | "press" | "internal";
  icon: typeof Globe2;
  pulled: string;
};

const SOURCES: Source[] = [
  {
    id: 1,
    org: "IPCC",
    title: "AR6 Synthesis Report · Mitigation Pathways",
    kind: "intergov",
    icon: Globe2,
    pulled: "p. 118",
  },
  {
    id: 2,
    org: "OECD",
    title: "Climate-finance flows 2018-2024",
    kind: "thinktank",
    icon: Building2,
    pulled: "Tbl. 4",
  },
  {
    id: 3,
    org: "Reuters",
    title: "EU adopts CBAM expansion · 2026 timeline",
    kind: "press",
    icon: Newspaper,
    pulled: "lede",
  },
  {
    id: 4,
    org: "Internal",
    title: "Risk note · Scope-3 attribution methodology",
    kind: "internal",
    icon: FileText,
    pulled: "§2.1",
  },
];

const KIND_LABEL: Record<Source["kind"], string> = {
  intergov: "Intergov",
  thinktank: "Think-tank",
  press: "Press",
  internal: "Internal",
};

// The sentence being drafted, broken into typed chunks so we can drop the
// citation pill in at exactly the right moment.
const DRAFT_PREFIX =
  "Carbon-border adjustment regimes are accelerating: the EU expansion lands in 2026, ";
const DRAFT_CITED_CLAUSE =
  "shifting an estimated USD 18bn in trade-flow attribution onto importers";
const DRAFT_SUFFIX =
  " — and tightening the price of carbon leakage outside the bloc.";

type AgentStep = {
  label: string;
  status: "queued" | "running" | "done";
  detail?: string;
};

// Partial-fill: search corpus already complete + ranking running so the agent
// panel reads "in flight" rather than "everything queued" at first paint.
const STEPS_INITIAL: AgentStep[] = [
  { label: "Search corpus", status: "done", detail: "38 sources" },
  { label: "Rank by salience", status: "done", detail: "top-4 selected" },
  { label: "Draft paragraph", status: "running" },
  { label: "Insert citations", status: "queued", detail: "0 / 4" },
];

export function ResearchMock() {
  const [steps, setSteps] = useState<AgentStep[]>(STEPS_INITIAL);
  // Combined string we type through char-by-char.
  const FULL_DRAFT = DRAFT_PREFIX + DRAFT_CITED_CLAUSE + DRAFT_SUFFIX;
  const CITED_END = DRAFT_PREFIX.length + DRAFT_CITED_CLAUSE.length;
  // First-paint: prefix already typed (paragraph reads as half-drafted, no
  // empty draft pane).
  const INITIAL_TYPED = DRAFT_PREFIX.length;

  const [typed, setTyped] = useState(INITIAL_TYPED); // chars typed so far
  const [citationIn, setCitationIn] = useState(false);
  const [activeSource, setActiveSource] = useState(2); // 0-indexed; Reuters glows first
  const [hoverSource, setHoverSource] = useState<number | null>(null);
  const [status, setStatus] = useState<"idle" | "running" | "complete">("running");
  const [tab, setTab] = useState<"page" | "notes">("page");
  const [loopCount, setLoopCount] = useState(0);
  const ref = useRef<HTMLDivElement>(null);
  const armed = useRef(false);
  const timers = useRef<number[]>([]);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;

    const clearTimers = () => {
      timers.current.forEach((t) => clearTimeout(t));
      timers.current = [];
    };
    const schedule = (fn: () => void, delay: number) => {
      const id = window.setTimeout(fn, delay);
      timers.current.push(id);
    };

    const setStepStatus = (i: number, st: AgentStep["status"], detail?: string) => {
      setSteps((prev) =>
        prev.map((s, idx) => (idx === i ? { ...s, status: st, detail: detail ?? s.detail } : s))
      );
    };

    // Loop reset target = the same partial-fill snapshot first paint shows.
    const reset = () => {
      setSteps(STEPS_INITIAL);
      setTyped(INITIAL_TYPED);
      setCitationIn(false);
      setActiveSource(2);
      setStatus("running");
    };

    const runCycle = () => {
      reset();

      // Search + rank are already "done" in the initial state — skip straight
      // to drafting from the partial-typed point.
      const draftStart = 400;
      for (let i = INITIAL_TYPED + 1; i <= FULL_DRAFT.length; i += 1) {
        schedule(() => {
          setTyped(i);
          if (i === CITED_END) setCitationIn(true);
        }, draftStart + (i - INITIAL_TYPED) * 14);
      }
      schedule(
        () => setStepStatus(2, "done"),
        draftStart + (FULL_DRAFT.length - INITIAL_TYPED) * 14 + 80
      );

      // Step 3: cite — bump active source
      const citeStart = draftStart + (FULL_DRAFT.length - INITIAL_TYPED) * 14 + 200;
      [1, 0, 3, 2].forEach((idx, n) => {
        schedule(() => setActiveSource(idx), citeStart + n * 250);
        schedule(() => setStepStatus(3, "running", `${n + 1} / 4`), citeStart + n * 250);
      });
      schedule(() => {
        setStepStatus(3, "done", "4 / 4");
        setStatus("complete");
      }, citeStart + 4 * 250 + 200);

      // Loop: pause then restart.
      const endAt = citeStart + 4 * 250 + 200;
      schedule(() => {
        setLoopCount((c) => c + 1);
        runCycle();
      }, endAt + 2800);
    };

    const obs = new IntersectionObserver(
      ([entry]) => {
        if (!entry.isIntersecting || armed.current) return;
        armed.current = true;
        schedule(runCycle, 400);
        obs.disconnect();
      },
      { threshold: 0.3 }
    );
    obs.observe(el);
    return () => {
      obs.disconnect();
      clearTimers();
    };
  }, [FULL_DRAFT.length, CITED_END, INITIAL_TYPED]);

  const draftHead = FULL_DRAFT.slice(0, Math.min(typed, CITED_END));
  const draftTail =
    typed > CITED_END ? FULL_DRAFT.slice(CITED_END, typed) : "";

  return (
    <div ref={ref} className="relative w-full max-w-5xl mx-auto">
      <div aria-hidden className="absolute -inset-x-4 -inset-y-4 rounded-[28px] bg-foreground/[0.02] blur-2xl" />

      <div className="relative rounded-[20px] border hairline-strong bg-card/80 backdrop-blur-md overflow-hidden shadow-[0_24px_48px_-24px_hsl(var(--foreground)/0.18)]">
        {/* Window chrome */}
        <div className="flex items-center gap-2 px-4 h-10 border-b hairline">
          <span className="h-2.5 w-2.5 rounded-full bg-foreground/15" />
          <span className="h-2.5 w-2.5 rounded-full bg-foreground/15" />
          <span className="h-2.5 w-2.5 rounded-full bg-foreground/15" />
          <span className="ml-3 text-[11px] tracking-[0.18em] uppercase text-foreground/65 font-mono">
            research.askfinz.ai · live
          </span>
          <span className="ml-auto inline-flex items-center gap-1.5 text-[10px] tracking-[0.16em] uppercase text-foreground/65">
            <span
              className={
                "h-1.5 w-1.5 rounded-full " +
                (status === "running"
                  ? "bg-[hsl(var(--accent))] animate-pulse"
                  : status === "complete"
                  ? "bg-foreground/70"
                  : "bg-foreground/20")
              }
            />
            {status === "running" ? "Drafting" : status === "complete" ? "Cited" : "Idle"}
          </span>
        </div>

        <div className="grid grid-cols-12 min-h-[460px] md:min-h-[520px]">
          {/* Sources rail */}
          <aside className="col-span-12 md:col-span-4 border-b md:border-b-0 md:border-r hairline bg-foreground/[0.02]">
            <div className="flex items-center gap-2 px-3 py-2 border-b hairline text-[10px] tracking-[0.16em] uppercase text-foreground/65">
              <Search className="h-3 w-3" />
              <span>Sources · 4 cited</span>
              <span className="ml-auto text-foreground/65 font-mono">38 found</span>
            </div>
            <div className="p-3 space-y-2">
              {SOURCES.map((s, i) => {
                const Icon = s.icon;
                const isActive = activeSource === i;
                const isHover = hoverSource === i;
                return (
                  <div
                    key={s.id}
                    onMouseEnter={() => setHoverSource(i)}
                    onMouseLeave={() => setHoverSource(null)}
                    className={
                      "rounded-xl border p-3 transition-all cursor-default " +
                      (isActive
                        ? "hairline-strong bg-background shadow-sm -translate-y-[1px]"
                        : isHover
                        ? "hairline-strong bg-background"
                        : "hairline bg-card/60")
                    }
                  >
                    <div className="flex items-center gap-2">
                      <span className="inline-flex h-6 w-6 items-center justify-center rounded-md border hairline-strong bg-background">
                        <Icon className="h-3 w-3" />
                      </span>
                      <span className="text-[10px] font-mono tracking-[0.14em] uppercase text-foreground/65">
                        {KIND_LABEL[s.kind]} · {s.org}
                      </span>
                      <span
                        className={
                          "ml-auto inline-flex h-5 min-w-[20px] px-1.5 items-center justify-center rounded-md text-[10px] font-mono " +
                          (isActive
                            ? "bg-[hsl(var(--accent))] text-background"
                            : "border hairline-strong text-foreground/65")
                        }
                      >
                        [{s.id}]
                      </span>
                    </div>
                    <div className="mt-2 text-[12.5px] leading-snug text-foreground/85">
                      {i === 2 ? (
                        <ScrambleText text={s.title} className="font-sans" duration={900} />
                      ) : (
                        s.title
                      )}
                    </div>
                    <div className="mt-1.5 flex items-center gap-1.5 text-[10px] text-foreground/65">
                      <Quote className="h-2.5 w-2.5" />
                      <span className="font-mono">{s.pulled}</span>
                      <span className="ml-auto font-mono text-foreground/65">
                        {isActive ? "ranked #" + (i + 1) : `salience ${(0.92 - i * 0.08).toFixed(2)}`}
                      </span>
                    </div>
                    {isHover && (
                      <div className="mt-2 pt-2 border-t hairline grid grid-cols-3 gap-2 text-[9px] font-mono tracking-[0.12em] uppercase text-foreground/65 animate-[blur-fade-in_0.3s_ease-out]">
                        <div><span className="text-foreground/65">cached</span><div className="text-foreground/80 normal-case tracking-normal">{["12m", "1h", "3h", "1d"][i]} ago</div></div>
                        <div><span className="text-foreground/65">retrievals</span><div className="text-foreground/80 normal-case tracking-normal">{[14, 8, 21, 5][i]}</div></div>
                        <div><span className="text-foreground/65">trust</span><div className="text-foreground/80 normal-case tracking-normal">{["A+", "A", "A", "B+"][i]}</div></div>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </aside>

          {/* Draft pane */}
          <main className="col-span-12 md:col-span-8 flex flex-col">
            <div className="flex items-center border-b hairline text-[12px] font-mono">
              <button
                type="button"
                onClick={() => setTab("page")}
                className={
                  "px-3 h-9 inline-flex items-center gap-2 border-r hairline transition " +
                  (tab === "page" ? "bg-background" : "text-foreground/65 hover:text-foreground/70")
                }
              >
                <FileText className="h-3 w-3 text-foreground/65" />
                <span>cbam-brief.page</span>
              </button>
              <button
                type="button"
                onClick={() => setTab("notes")}
                className={
                  "px-3 h-9 inline-flex items-center gap-2 border-r hairline transition " +
                  (tab === "notes" ? "bg-background" : "text-foreground/65 hover:text-foreground/70")
                }
              >
                <FileText className="h-3 w-3" />
                <span>scope-3-notes.page</span>
              </button>
              <div className="ml-auto pr-3 text-[10px] tracking-[0.16em] uppercase text-foreground/65">
                Pages · v.{3 + loopCount}
              </div>
            </div>

            <div className={"flex-1 px-6 py-5 bg-background overflow-hidden " + (tab === "notes" ? "hidden" : "")}>
              <div className="text-[10px] tracking-[0.16em] uppercase text-foreground/65 font-mono mb-2">
                § 2 · Border-adjustment regimes
              </div>
              <h3 className="text-[18px] font-medium tracking-tight text-foreground/90 mb-3">
                What ships when the CBAM expansion lands.
              </h3>
              <p className="text-[14px] leading-[1.7] text-foreground/80">
                The bloc&#39;s carbon-border framework was designed as a leakage hedge; the
                2026 expansion redraws the perimeter — and the cost of admission.
              </p>
              <p className="mt-3 text-[14px] leading-[1.7] text-foreground/80">
                {draftHead}
                {/* The cited clause itself, with subtle highlight while it's being typed */}
                {draftTail && (
                  <span
                    className={
                      "transition-colors " +
                      (citationIn
                        ? "text-foreground/90"
                        : "bg-[hsl(var(--accent))]/8 text-foreground/90")
                    }
                  >
                    {draftTail}
                  </span>
                )}
                {/* Citation pill */}
                {citationIn && (
                  <span className="inline-flex items-center align-baseline">
                    <span className="ml-0.5 inline-flex h-5 min-w-[24px] px-1.5 items-center justify-center rounded-md bg-[hsl(var(--accent))] text-background text-[10px] font-mono animate-[blur-fade-in_0.45s_ease-out]">
                      [3]
                    </span>
                  </span>
                )}
                {/* Caret */}
                {typed < FULL_DRAFT.length && (
                  <span
                    aria-hidden
                    className="inline-block ml-0.5 h-[1em] w-[1.5px] align-middle bg-[hsl(var(--accent))]/80 animate-pulse"
                  />
                )}
              </p>

              {status === "complete" && (
                <div className="mt-6 flex items-center gap-2 text-[11px] font-mono text-foreground/65 animate-[blur-fade-in_0.5s_ease-out]">
                  <Sparkles className="h-3 w-3" />
                  Citations [1] [2] [3] [4] inserted · footnote chain preserved through merges
                </div>
              )}
            </div>

            {tab === "notes" && (
              <div className="flex-1 px-6 py-5 bg-background overflow-hidden animate-[blur-fade-in_0.4s_ease-out]">
                <div className="text-[10px] tracking-[0.16em] uppercase text-foreground/65 font-mono mb-2">
                  Scratch · scope-3 attribution
                </div>
                <h3 className="text-[18px] font-medium tracking-tight text-foreground/90 mb-3">
                  Methodology notes.
                </h3>
                <ul className="space-y-2 text-[13px] leading-[1.65] text-foreground/80">
                  <li className="flex gap-2"><span className="text-foreground/35">·</span>Spend-based proxies dominate for tier-1 importers; physical-flow is rare.</li>
                  <li className="flex gap-2"><span className="text-foreground/35">·</span>The PCAF guidance is the closest thing to an industry consensus.</li>
                  <li className="flex gap-2"><span className="text-foreground/35">·</span>Allocation factor varies 3x across the cement supply chain.</li>
                  <li className="flex gap-2"><span className="text-foreground/35">·</span>Worth threading through to the importer-cost math in §3.</li>
                </ul>
                <div className="mt-5 text-[10px] tracking-[0.16em] uppercase text-foreground/65 font-mono">
                  Last edited 4m ago · 412 words · 3 unresolved comments
                </div>
              </div>
            )}

            {/* Agent panel */}
            <div className="border-t hairline bg-foreground/[0.02] px-3 py-2.5">
              <div className="flex items-center gap-2 text-[10px] tracking-[0.16em] uppercase text-foreground/65 mb-2">
                <Bot className="h-3 w-3" />
                <span>Agent · Researcher</span>
                <Sparkles className="h-3 w-3 ml-auto text-foreground/65" />
              </div>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
                {steps.map((s, i) => (
                  <div
                    key={i}
                    className={
                      "rounded-lg border px-2.5 py-2 text-[11px] flex items-start gap-2 " +
                      (s.status === "done"
                        ? "hairline-strong bg-background"
                        : s.status === "running"
                        ? "hairline-strong bg-[hsl(var(--accent))]/4"
                        : "hairline bg-card/40 text-foreground/65")
                    }
                  >
                    <span className="mt-0.5 shrink-0">
                      {s.status === "done" ? (
                        <CheckCircle2 className="h-3 w-3 text-[hsl(var(--accent))]" />
                      ) : s.status === "running" ? (
                        <Play className="h-3 w-3 text-foreground/70 animate-pulse" />
                      ) : (
                        <span className="block h-3 w-3 rounded-full border hairline-strong" />
                      )}
                    </span>
                    <div className="min-w-0">
                      <div className="text-foreground/85 leading-tight">{s.label}</div>
                      {s.detail && (
                        <div className="mt-0.5 text-[10px] font-mono text-foreground/65">{s.detail}</div>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </main>
        </div>
      </div>

      <div className="mt-4 flex items-center justify-center gap-2 text-[11px] uppercase tracking-[0.18em] text-foreground/65">
        <ArrowUpRight className="h-3 w-3" />
        <span>
          Live product at <span className="font-mono text-foreground/65">research.askfinz.ai</span>
        </span>
      </div>
    </div>
  );
}

export default ResearchMock;
